import { useEffect, useMemo, useState } from 'react';
import { getToken } from '../authentication/functions/authentication_management';
import {
  ADMIN_CAPABILITY_EVENT,
  decodeAuthTokenClaims,
  getCachedAdminCapability,
  probeAdminCapability,
  resolveIsAdminFromSources,
} from '../../services/auth/adminAccess';

export function useResolvedAdminAccess(): boolean {
  const token = getToken();
  const decoded = useMemo(() => decodeAuthTokenClaims(token), [token?.access_token]);
  const [apiCapability, setApiCapability] = useState<boolean | null>(() => getCachedAdminCapability());

  useEffect(() => {
    if (!token?.access_token) {
      setApiCapability(null);
      return;
    }

    let active = true;
    // Force a backend re-check on session/token changes to avoid stale cached admin state.
    void probeAdminCapability(true).then((allowed) => {
      if (!active) return;
      setApiCapability(allowed);
    });

    const onCapabilityUpdated = () => {
      if (!active) return;
      setApiCapability(getCachedAdminCapability());
    };
    window.addEventListener(ADMIN_CAPABILITY_EVENT, onCapabilityUpdated);

    return () => {
      active = false;
      window.removeEventListener(ADMIN_CAPABILITY_EVENT, onCapabilityUpdated);
    };
  }, [token?.access_token]);

  return resolveIsAdminFromSources(decoded, apiCapability);
}
